import React, { useState, useRef } from "react";
import { Check, X, ArrowRight, SlidersHorizontal, Sparkles, Layers } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { PackageBuilder } from "@/components/PackageBuilder";
import { PRESETS, COMPARISON } from "@/data/pricing";
import { selectionFromIds, fmt } from "@/lib/pricingEngine";

const CellIcon = ({ v }) => {
  if (v === true) return <Check className="h-4 w-4 text-brandgreen" />;
  if (v === false) return <X className="h-4 w-4 text-crimson/70" />;
  return <span className="text-[0.7rem] font-bold text-brandgreen">{v}</span>;
};

export default function Packages() {
  const { t, isAr } = useLang();
  const builderRef = useRef(null);
  const [initSel, setInitSel] = useState({});
  const [initKey, setInitKey] = useState(0);

  const openBuilder = (ids) => {
    setInitSel(selectionFromIds(ids));
    setInitKey((k) => k + 1);
    setTimeout(() => {
      const el = document.getElementById("builder");
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
      else el?.scrollIntoView({ behavior: "smooth" });
    }, 90);
  };

  return (
    <div className="pt-28 lg:pt-32 pb-24">
      {/* Header */}
      <section className="container-tnsq">
        <div className="max-w-3xl">
          <Reveal><span className="kicker gold-line">{t({ en: "Our packages", ar: "باقاتنا" })}</span></Reveal>
          <Reveal delay={80}>
            <h1 className="font-display text-4xl sm:text-5xl text-ink mt-5 leading-[1.1]">
              {t({ en: "Ready-made plans — or build your own, service by service.", ar: "باقات جاهزة — أو ابنِ باقتك خدمةً بخدمة." })}
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-5 text-muted text-base md:text-lg leading-relaxed">
              {t({ en: "Every service has a clear, calculable price. Choose one of four packages, or design a package tailored exactly to your business and see the total instantly.", ar: "لكل خدمة سعر واضح وقابل للحساب. اختر إحدى الباقات الأربع، أو صمّم باقة تناسب أعمالك تماماً وشاهد الإجمالي فوراً." })}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Preset cards */}
      <section className="container-tnsq mt-12">
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
          {PRESETS.map((p, idx) => {
            return (
              <Reveal key={p.id} delay={(idx % 4) * 70} className="h-full">
                <div data-testid={`preset-${p.id}`}
                  className={`relative flex h-full flex-col rounded-[1.5rem] bg-white p-6 transition-shadow duration-300 ${
                    p.popular ? "border-2 border-crimson shadow-lift" : "border border-line shadow-card hover:shadow-lift"
                  }`}>
                  <span className={`absolute -top-3 start-6 inline-flex items-center gap-1 text-[0.65rem] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${
                    p.popular ? "bg-crimson text-white" : "bg-navy text-white"
                  }`}>
                    {p.popular && <Sparkles className="h-3 w-3" />} {t(p.badge)}
                  </span>
                  <h3 className="font-display text-2xl text-navy mt-2">{t(p.name)}</h3>
                  <p className="mt-2 text-sm text-muted leading-snug min-h-[64px]">{t(p.desc)}</p>
                  <div className="mt-3 rounded-xl bg-surface border border-line px-4 py-3">
                    <div className="font-display text-3xl text-ink" dir="ltr">{fmt(p.price)}<span className="text-sm text-muted font-body"> {t({ en: "/mo", ar: "/شهر" })}</span></div>
                    <div className="text-[0.72rem] text-muted mt-1">{t({ en: "Up to", ar: "حتى" })} {p.docAllowance} {t({ en: "documents / month", ar: "مستند / شهرياً" })}</div>
                  </div>

                  <div className="mt-5 space-y-2">
                    {COMPARISON.map((row, ri) => {
                      const val = row.v[idx];
                      const on = val !== false;
                      return (
                        <div key={ri} className="flex items-center justify-between gap-2 text-[0.82rem]">
                          <span className={on ? "text-ink/80" : "text-muted/60 line-through decoration-crimson/30"}>{t(row.label)}</span>
                          <span className="shrink-0"><CellIcon v={val} /></span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 flex flex-col gap-2.5">
                    <button onClick={() => openBuilder(p.services)} data-testid={`choose-${p.id}`}
                      className={`inline-flex items-center justify-center h-11 rounded-full font-semibold transition-colors ${
                        p.popular ? "bg-crimson text-white hover:bg-crimson-600" : "bg-navy text-white hover:bg-navy-600"
                      }`}>
                      {t({ en: "Choose package", ar: "اختر الباقة" })}
                    </button>
                    <button onClick={() => openBuilder(p.services)} data-testid={`customize-${p.id}`}
                      className="inline-flex items-center justify-center gap-1.5 h-11 rounded-full border border-navy/25 text-navy font-semibold hover:bg-navy hover:text-white transition-colors">
                      <SlidersHorizontal className="h-4 w-4" /> {t({ en: "Customize", ar: "تخصيص" })}
                    </button>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Comparison table (desktop) */}
      <section className="container-tnsq mt-16 hidden lg:block">
        <Reveal>
          <div className="rounded-[1.5rem] border border-line bg-white shadow-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy text-white">
                  <th className="text-start font-semibold px-6 py-4">{t({ en: "Feature", ar: "الميزة" })}</th>
                  {PRESETS.map((p) => (<th key={p.id} className="px-4 py-4 font-semibold text-center">{t(p.name)}</th>))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, ri) => (
                  <tr key={ri} className={ri % 2 ? "bg-surface/60" : "bg-white"}>
                    <td className="px-6 py-3 text-ink/80">{t(row.label)}</td>
                    {row.v.map((v, ci) => (<td key={ci} className="px-4 py-3"><div className="flex justify-center"><CellIcon v={v} /></div></td>))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </section>

      {/* Build Your Own — big card */}
      <section className="container-tnsq mt-16">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] bg-navy-deep px-7 py-10 md:px-14 md:py-14">
            <div className="absolute -top-16 -end-16 h-64 w-64 rounded-full bg-crimson/20 blur-3xl" />
            <div className="relative flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 text-crimson-soft text-xs font-bold uppercase tracking-wider bg-crimson/25 px-3 py-1 rounded-full">
                  <Layers className="h-3.5 w-3.5" /> {t({ en: "Most flexible", ar: "الأكثر مرونة" })}
                </span>
                <h2 className="font-display text-3xl md:text-4xl text-white mt-4 leading-tight">
                  {t({ en: "Build Your Own Package", ar: "ابنِ باقتك الخاصة" })}
                </h2>
                <p className="mt-3 text-white/70 text-base leading-relaxed">
                  {t({ en: "Your business is different. Your accounting package can be too. Choose the accounting, tax, reporting, payroll and financial management services your business needs — and see your total price instantly.", ar: "أعمالك مختلفة، وباقتك المحاسبية يمكن أن تكون كذلك. اختر خدمات المحاسبة والضرائب والتقارير والرواتب والإدارة المالية التي يحتاجها عملك — وشاهد الإجمالي فوراً." })}
                </p>
              </div>
              <button onClick={() => openBuilder([])} data-testid="build-my-package"
                className="shrink-0 inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-crimson text-white font-semibold text-lg hover:bg-crimson-600 transition-colors">
                {t({ en: "Build my package", ar: "ابنِ باقتي" })} <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Builder */}
      <section id="builder" className="container-tnsq mt-16 scroll-mt-24 pb-20 lg:pb-0" data-testid="builder-section">
        <Reveal>
          <div className="max-w-2xl mb-8">
            <span className="kicker gold-line">{t({ en: "Service marketplace", ar: "سوق الخدمات" })}</span>
            <h2 className="font-display text-3xl sm:text-4xl text-ink mt-4">{t({ en: "Configure your package", ar: "خصّص باقتك" })}</h2>
            <p className="mt-3 text-muted">{t({ en: "Add the services you need, adjust your volumes, and watch your monthly, one-time and annual totals update live.", ar: "أضف الخدمات التي تحتاجها، اضبط الأحجام، وشاهد إجمالياتك الشهرية ولمرة واحدة والسنوية تتحدث مباشرةً." })}</p>
          </div>
        </Reveal>
        <PackageBuilder initial={initSel} initKey={initKey} />
      </section>
    </div>
  );
}
